/**
 * The persisted recipe document.
 *
 * This is the thing that syncs, versions, exports and gets handed to the AI
 * assistant. It is **not** what the maths consumes: `../bridge/calculation.ts`
 * maps it onto core's `RecipeInput` at the edge (PLAN §5, "conversion only at
 * the edges").
 *
 * Every field is reachable by exactly one command — there is no back door that
 * writes a recipe without going through the log (PLAN §8.3).
 */

import { gallonsToLitres } from '@beerwright/core';
import { z } from 'zod';
import { RECIPE_SCHEMA_VERSION } from '../version';
import {
  batchVolumeBasisSchema,
  documentIdSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  packagingMethodSchema,
  percentSchema,
  positiveSchema,
  primingSugarSchema,
  specificGravitySchema,
  tagsSchema,
  timestampSchema,
} from './common';
import {
  liftFermentableYield,
  recipeFermentableSchema,
  recipeFermentationStepSchema,
  recipeHopSchema,
  recipeMashStepSchema,
  recipeMiscSchema,
  recipeYeastSchema,
} from './ingredients';
import { recipeWaterSchema } from './water';

/** Batch volume a blank recipe starts at, litres — 5 US gallons. */
export const DEFAULT_BATCH_VOLUME_L = gallonsToLitres(5);

/** Boil length a blank recipe starts at, minutes. */
export const DEFAULT_BOIL_TIME_MINUTES = 60;

/** Carbonation a blank recipe starts at, volumes of CO₂ (core's default). */
export const DEFAULT_CARBONATION_VOLUMES = 2.4;

/** The fields of a persisted recipe. @see recipeDocSchema */
export const recipeDocFieldsSchema = z.strictObject({
  /** Document schema version; see {@link RECIPE_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Stable recipe id, unique within the workspace. */
  id: documentIdSchema,
  /** Recipe name. */
  name: nameSchema,
  /**
   * BJCP style id from the catalog (`bjcp-2021-21a`), or `null` for
   * "no declared style". Stored as an id, not a snapshot: style ranges are
   * guideline text, not a per-batch measurement, and a recipe should follow the
   * guidelines it is judged against.
   */
  styleId: z.string().min(1).max(80).nullable(),
  /**
   * The OG this recipe is aiming at, or `null` to follow the style.
   *
   * An *intention*, not a prediction. The engine's OG is computed from the
   * grist and lives on the calculation; this is the brewer saying what they
   * meant, which is the number the percent-mode grist solves its total mass
   * against and the number a filling base malt is sized to when it is added.
   *
   * `null` rather than a copy of the style's midpoint, for the reason
   * {@link mashEfficiencyPercentOverride} is `null` rather than a copy of the
   * profile's efficiency: a recipe that never stated a target should keep
   * tracking what it is. Restyle an ordinary bitter as a strong one and the
   * target moves with it, which is what somebody who never typed a number
   * meant. `store/add-ingredient.ts` resolves the fallback — this document
   * deliberately does not know what a style is.
   *
   * Defaulted, so a recipe written before this existed loads as "no target" —
   * see {@link packagingMethod} for why defaulting is safe on this document.
   */
  targetOg: specificGravitySchema.nullable().default(null),
  /**
   * The colour this recipe is aiming at, **SRM**, or `null` to follow the style.
   *
   * The same kind of field as {@link targetOg}, on the other number a grist
   * decides: an intention rather than a prediction, `null` meaning "whatever
   * this style is" so a restyled recipe keeps tracking it, and the fallback
   * resolved in `store/add-ingredient.ts` where the style band is known.
   *
   * SRM rather than EBC because SRM is the engine's own colour unit
   * (`color.ts`); a workspace set to EBC converts on the way in and out, as it
   * does for every other colour on screen. Bounded at 200, which is past the
   * darkest imperial stout anyone has measured and well short of a typo.
   */
  targetSrm: nonNegativeSchema.max(200).nullable().default(null),
  /** Target batch volume, **litres**, measured at {@link batchVolumeBasis}. */
  batchVolumeL: positiveSchema.max(10_000),
  /** What {@link batchVolumeL} measures. */
  batchVolumeBasis: batchVolumeBasisSchema,
  /** Boil duration, **minutes**. */
  boilTimeMinutes: z.number().finite().min(0).max(1440),
  /**
   * Equipment profile id, or `null` for "the workspace default".
   *
   * A reference rather than a snapshot on purpose: the equipment profile
   * describes the brewer's *system as it is today*, so a recalibrated boil-off
   * rate should apply to every recipe on that system at once. Contrast the
   * ingredient snapshots, where the opposite is true (PLAN §6).
   */
  equipmentProfileId: documentIdSchema.nullable(),
  /**
   * Mash efficiency for *this recipe*, **%**, or `null` to use the profile's.
   *
   * The profile describes the rig; this describes one beer on it. A brewer
   * knows before dough-in that a 1.090 imperial stout will lauter worse than
   * their pale ale, that a 40 % wheat grist will run slow, that a
   * flaked-adjunct bill converts less well — and the answer to that has never
   * been "duplicate your equipment profile with a different number in it". A
   * profile per beer is a profile library nobody can maintain: recalibrate
   * boil-off next spring and you have to do it in nine places.
   *
   * So the override lives here, on the recipe, and it is deliberately narrow:
   * **mash efficiency only**. Everything else the profile carries is a property
   * of the hardware (losses, absorption, boil-off, thermal mass) and does not
   * change because the grist did. Brewhouse efficiency is not overridable
   * because it is not an input at all — the engine predicts it from this figure
   * and the volume waterfall.
   *
   * `null` rather than a copy of the profile's number, so a recipe that has not
   * opted out keeps tracking the rig: recalibrate the profile and every recipe
   * that never overrode moves with it. Defaulted, so a recipe written before
   * this existed loads as "no override" — see {@link packagingMethod} for why
   * defaulting is safe on this document in particular.
   */
  mashEfficiencyPercentOverride: percentSchema.nullable().default(null),
  /** Target carbonation, **volumes of CO₂**. */
  carbonationVolumes: z.number().finite().min(0).max(6),
  /**
   * How this beer gets packaged, and what it is primed with.
   *
   * Both carry a `.default()` so a recipe written before these existed still
   * loads — the packaging panel used to hold them as local state, which meant a
   * keg brewer re-picked "Keg" on every reload. Defaulting is safe here in a way
   * it is not elsewhere in this file: the recipe document has no `.partial()`
   * patch command, so there is no path by which an update that omits these can
   * materialise them and overwrite what the brewer chose. Contrast
   * `recipeMashStepChangesSchema` and the equipment profile patch, which both
   * had to restate defaulted keys as `.optional()` for exactly that reason.
   *
   * {@link primingSugar} stays meaningful under `keg`, which is forced
   * carbonation and uses no sugar at all: the brewer's choice survives a trip
   * through the keg setting and back rather than resetting to corn sugar.
   */
  packagingMethod: packagingMethodSchema.default('bottle'),
  /** What a conditioning package is primed with. @see packagingMethod */
  primingSugar: primingSugarSchema.default('corn-sugar'),
  /** Grist and sugars, in display order. */
  fermentables: z.array(recipeFermentableSchema).max(64),
  /** Hop schedule, in display order. */
  hops: z.array(recipeHopSchema).max(64),
  /** Yeast pitches, in display order. */
  yeasts: z.array(recipeYeastSchema).max(16),
  /** Miscellaneous additions, in display order. */
  miscs: z.array(recipeMiscSchema).max(64),
  /** Mash schedule, in execution order. */
  mashSteps: z.array(recipeMashStepSchema).max(16),
  /** Fermentation schedule, in execution order. */
  fermentationSteps: z.array(recipeFermentationStepSchema).max(16),
  /** Water & mash-pH section, or `null` when the recipe does not use one. */
  water: recipeWaterSchema.nullable(),
  /** Brewer's prose. */
  notes: notesSchema,
  /** User-defined labels. */
  tags: tagsSchema,
  /** Creation time, **ms since epoch**, from the creating command's envelope. */
  /**
   * When this recipe was put away, or `null` while it is in the list.
   *
   * A timestamp rather than a flag, because the archived view's one extra
   * column is "when did I put this away" and a boolean cannot answer it. The
   * reducer stamps it from `command.timestamp` — the clock `withRecipe`
   * already uses for {@link updatedAt} — so replaying a log lands on the same
   * date it did the first time.
   *
   * It changes what the *list* shows and nothing else. An archived recipe is
   * still held, still synced, still counted against the plan's recipe cap, and
   * a share link or a gallery listing pointing at it keeps working: putting
   * something away is not the same as withdrawing it.
   *
   * Defaulted, so a recipe written before this existed loads as "in the list" —
   * see {@link packagingMethod} for why defaulting is safe on this document.
   */
  archivedAt: timestampSchema.nullable().default(null),
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**, from the last command's envelope. */
  updatedAt: timestampSchema,
});

/**
 * Read a recipe saved when a file's `YIELD` lived in `ppgOverride`.
 *
 * ## Why the gate is the document's stamp and not the field
 *
 * Every other migration in this model keys off a *shape* — a key present or
 * absent — because a shape cannot be mistaken for something a brewer chose.
 * This one cannot: `ppgOverride` is a real, editable field, and from schema 3
 * on a non-null value in it is a yield the brewer measured off their own sack.
 * A lift keyed on "the field is set" would delete that measurement on the next
 * read, every read, and the field would be permanently unsettable.
 *
 * So the discriminator is {@link RecipeDoc.schemaVersion}: **under 3, a non-null
 * override was written by the importer** — no editor for the field existed and
 * the assistant could not see it — and at 3 or above it is the brewer's. That
 * makes the stamp load-bearing for the first time, which is why this migration
 * rewrites it and `migrateRecipeWater` does not.
 *
 * ## What it guarantees
 *
 * **Idempotent.** The output is stamped 3, so a second read is a no-op; running
 * it twice cannot differ from running it once, which is what makes it safe on
 * *every* read.
 *
 * **It reaches everything, without a walker.** A batch's `recipeSnapshot` and a
 * named version's `recipe` are declared as this schema, so the lift is simply
 * part of reading them. `version.ts` anticipated a migration that would have to
 * "walk into batches without guessing"; it turned out not to need to walk.
 *
 * **It cannot half-apply.** It is a pure function inside the parse. A workspace
 * either arrives with every document lifted or fails validation as a whole and
 * is treated as absent by its reader — the Durable Object rebuilds from its
 * command log, the browser re-syncs. Nothing is rewritten in storage as a side
 * effect; the lifted form is persisted the next time its own writer persists.
 *
 * A document whose `schemaVersion` is missing or not a number is left entirely
 * alone rather than being stamped into validity: this is a migration, not a
 * repair, and inventing a stamp would hide a genuinely broken document.
 */
export function migrateRecipeExtractScale(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  const stamped = rec.schemaVersion;
  if (typeof stamped !== 'number' || stamped >= RECIPE_SCHEMA_VERSION) return raw;
  if (!Array.isArray(rec.fermentables)) return { ...rec, schemaVersion: RECIPE_SCHEMA_VERSION };
  return {
    ...rec,
    schemaVersion: RECIPE_SCHEMA_VERSION,
    fermentables: rec.fermentables.map(liftFermentableYield),
  };
}

/** A persisted recipe. Older documents are lifted on the way in. */
export const recipeDocSchema = z.preprocess(migrateRecipeExtractScale, recipeDocFieldsSchema);

/** @see recipeDocFieldsSchema */
export type RecipeDoc = z.infer<typeof recipeDocFieldsSchema>;

/**
 * A blank recipe: named, sized, and otherwise empty.
 *
 * Pure — `id`, `name` and both timestamps come from the caller, so two replays
 * of the same log build byte-identical documents.
 *
 * @param args - Identity and creation time from the command envelope.
 */
export function blankRecipeDoc(args: {
  readonly id: string;
  readonly name: string;
  readonly timestamp: number;
}): RecipeDoc {
  return {
    schemaVersion: RECIPE_SCHEMA_VERSION,
    id: args.id,
    name: args.name,
    styleId: null,
    targetOg: null,
    targetSrm: null,
    batchVolumeL: DEFAULT_BATCH_VOLUME_L,
    batchVolumeBasis: 'fermenter',
    boilTimeMinutes: DEFAULT_BOIL_TIME_MINUTES,
    equipmentProfileId: null,
    mashEfficiencyPercentOverride: null,
    carbonationVolumes: DEFAULT_CARBONATION_VOLUMES,
    packagingMethod: 'bottle',
    primingSugar: 'corn-sugar',
    fermentables: [],
    hops: [],
    yeasts: [],
    miscs: [],
    mashSteps: [],
    fermentationSteps: [],
    water: null,
    notes: '',
    tags: [],
    archivedAt: null,
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}

/**
 * A copy of `recipe` with every ingredient link cleared.
 *
 * `ingredientId` is a key into the workspace a document was written in. When a
 * document crosses into another workspace — a shared recipe read by a stranger,
 * a clone of one — the key points at nothing there, or at something else
 * entirely, so it is stripped at the boundary rather than trusted (D-IL6,
 * D-IL8). Rows already unlinked are returned by reference, and so the whole
 * list is shared when nothing on it was linked.
 *
 * A boundary helper, deliberately not a reducer rule: `recipe.create { from }`
 * is also how a brewer duplicates their *own* recipe, and `recipe.restore`,
 * `recipe.revertTo` and the snapshot commands replay their own documents. Those
 * keep their links; an id that has since died is read as absent wherever it is
 * consumed (`linkedIngredientId` in `inventory/availability.ts`).
 */
export function stripIngredientLinks(recipe: RecipeDoc): RecipeDoc {
  const clear = <T extends { readonly ingredientId: string | null }>(items: readonly T[]): T[] =>
    items.map((item) => (item.ingredientId === null ? item : { ...item, ingredientId: null }));
  return {
    ...recipe,
    fermentables: clear(recipe.fermentables),
    hops: clear(recipe.hops),
    yeasts: clear(recipe.yeasts),
    miscs: clear(recipe.miscs),
  };
}
